import React, { Component } from 'react';
import { connect } from 'react-redux';
import get from 'lodash.get';
import PropTypes from 'prop-types';
import { TT911 } from '@rsos/capstone/src/constants/capabilities';
import { GOOGLE_MAPS_DECKGL_MARKERS } from '@rsos/capstone/src/constants/capabilities';
import { addAdrData } from '@rsos/components/capstone/DataCards/adrDataSlice';
import { isSourceAniAli } from '@rsos/components/capstone/Location/helpers';
import {
  getExistingDynamicObject,
  isSpatialLayerDynamic,
} from '@rsos/components/capstone/common/Map/utils/spatialLayersHelpers';
import { SOURCE_TCAPI } from '@rsos/constants/locationSources';
import { readFlags } from '@rsos/flags';
import StreamWS from '@rsos/hermes-js/src/StreamWS';
import { addAndUpdateAirAmbulanceRequest } from '@rsos/reduxDataStore/src/features/airAmbulance/airAmbulanceSlice';
import {
  resetIDWithNewAlert,
  updateAlert,
} from '@rsos/reduxDataStore/src/features/alerts/incidentsSlice';
import {
  addLocation,
  removeCall,
  setAdrQueryData,
  setAdrQueryStatus,
  setAgencyShareChatToastReceived,
  setCallChatParticipants,
  setCallReceived,
  setCallShared,
} from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { setIRPVersion } from '@rsos/sinatra';
import { getUrlParameter } from '@rsos/utils';
import exportToGlobalNamespace from '@rsos/utils/exportToGlobalNamespace';
import {
  getMostRecentLocations,
  getMostRecentLoc,
} from '@rsos/utils/getMostRecentLoc';
import { getAPIHost } from '@rsos/utils/metaTags';
import parseURI from '@rsos/utils/parseURI';
import { isValidPhone } from '@rsos/utils/validators';
import {
  addAlertToQueue,
  addDynamicLayer,
  addSAData,
  addLocToQueueSuccess,
  getAlertDetails,
  getLogMessageUpdatedStatus,
  setCallerID,
  setCandidates,
  setInitializedHermesState,
  setMafidToQuery,
  setMaLoadingState,
  setProfile,
  updateDynamicLayer,
} from '../../actions';
import { trackADRPayload } from '../../hermesTrackings';
import {
  parseWSMessage,
  ALERT,
  ALERT_LOG_MESSAGE,
  DATA,
  EVENT_RECEIVE,
  EVENT_SHARE,
  LOCATION,
  PARTICIPANT_ADDED,
  SPATIAL,
  SUBSCRIPTION,
} from '../../utils/wsMessage';
import { handleAviveRawMessage } from '../JurisdictionView/AviveAED/aviveSlice';
import { handleTranscriptionRawMessage } from '../JurisdictionView/EventPane/tabs/TranscriptTab/transcriptionSlice';
import { isExtraInfoOrMeta } from '../JurisdictionView/helpers';
import { handleAxonRawMessage } from '../Map/useDeckGL/Axon/axonSlice';
import { LAYERS as AXON_LAYERS } from '../Map/useDeckGL/Axon/useAxonGLNext';
import { handleEagleEyeRawMessage } from '../Map/useDeckGL/EagleEye/eagleEyeSlice';
import { LAYERS as EAGLE_EYE_LAYERS } from '../Map/useDeckGL/EagleEye/helpers';

const host = new URL(getAPIHost());
const webSocketHost =
  host.protocol !== 'https:' ? `ws://${host.host}` : `wss://${host.host}`;
const TYPE = 'type';
const TOPIC = 'topic';

// Normalize the message for `tel://...-adr` topics
const normalizeMessage = message => {
  const topic = message.topic;
  if (topic.startsWith('tel://') && topic.endsWith('-adr')) {
    return {
      ...message,
      topic: topic.split('-adr')[0],
    };
  }
  return { ...message };
};

class Hermes extends Component {
  constructor(props) {
    super(props);
    this.state = { adrQueryTimer: null };
    this.flags = readFlags();
  }

  componentDidMount() {
    window.addEventListener('beforeunload', () => {
      if (this.hermes) {
        this.hermes.closeConnection();
      }
    });

    const {
      accessToken,
      callerID,
      currentPSAP,
      hasAniAliAccess,
      isJVEnabled,
      isShareChatEnabled,
      pathName,
      selectedAlertID,
    } = this.props;

    if (pathName === '/irp-query-interface' || pathName === '/irp-home') {
      setIRPVersion('2');
    } else if (isValidPhone(getUrlParameter('caller_id'))) {
      let callerId = getUrlParameter('caller_id');
      if (callerId) {
        setIRPVersion('1');
      }
    }

    if (accessToken !== null && !this.hermes) {
      if (currentPSAP && currentPSAP.account_id) {
        if (isJVEnabled) {
          this.initializeHermes(true);
          // Subscribe to corresponding incident on page refresh for JV
          if (selectedAlertID) {
            this.subscribeToHermesWithAlertID(selectedAlertID);
          } else if (callerID) {
            this.subscribeToHermes(callerID);
          }
        } else if (hasAniAliAccess) {
          // QV scenario
          this.initializeHermes(true);
          // Subscribe to corresponding incident on page refresh for JV
          if (callerID) {
            this.subscribeToHermes(callerID);
          }
        } else if (isShareChatEnabled) {
          this.initializeHermes(false, true);
          if (callerID) this.subscribeToHermes(callerID);
        }
      } else if (!isJVEnabled && !isShareChatEnabled && callerID !== '') {
        this.initializeHermes();
        this.subscribeToHermes(callerID);
      }
    }
  }

  componentDidUpdate(prevProps) {
    const {
      avive,
      callerID,
      currentAdrQuery,
      currentMafid,
      currentPSAP,
      isJVEnabled,
      isJVHermesInitialized,
      isShareChatEnabled,
      isAviveEnabled,
      mafidToQuery,
      accessToken,
      selectedAlertID,
    } = this.props;

    if (isJVEnabled) {
      if (
        ((prevProps.currentPSAP.account_id !== currentPSAP.account_id &&
          webSocketHost &&
          accessToken !== null) ||
          (accessToken && prevProps.accessToken !== accessToken)) &&
        !isJVHermesInitialized
      ) {
        this.initializeHermes(true);
      }
    }

    if (
      (!isJVEnabled &&
        isShareChatEnabled &&
        prevProps.currentPSAP.account_id !== currentPSAP.account_id &&
        webSocketHost &&
        accessToken !== null) ||
      (accessToken && prevProps.accessToken !== accessToken)
    ) {
      this.initializeHermes(false, true);
    }

    // Update this.hermes._accessToken with the new token and make sure a
    // new websocket reconnection immediately happens after.
    if (
      prevProps.accessToken !== null &&
      prevProps.accessToken !== accessToken &&
      this.hermes &&
      currentPSAP?.account_id
    ) {
      this.hermes._accessToken = accessToken;
      // Make sure the Hermes client closes any open connection that might still
      // be using an expired or soon to be expired access token.
      this.hermes.closeConnection();
      // Re-subscribe the account ID, the handling inside of hermes.subscribe
      // will also do the reconnect for the websocket.
      this.hermes.subscribe(`account-id://${currentPSAP?.account_id}`);
    }

    // If callerID changes, subscribe to it
    if (accessToken) {
      if (!callerID && prevProps.callerID) {
        const prevTelKey = `tel://${prevProps.callerID}`;
        // When Avive search is active, do not unsubscribe to the previous
        // phone number. This can happen when an user searches for AED and
        // navigates away while the search is happening. Instead leave the
        // connection open until the search is over. When it's over the
        // isSearchActive will be set to false and normal unsubscribing behavior
        // can continue.
        if (
          !avive?.statuses[prevTelKey]?.states?.isSearchActive &&
          this.hermes
        ) {
          this.hermes.unsubscribe(prevTelKey);
        }
      }
      if (prevProps.callerID !== callerID && callerID !== '') {
        if (!isJVEnabled && !isShareChatEnabled && !isAviveEnabled) {
          if (this.hermes) {
            // NOTE: when unsubscribing/searching for new numbers, it doesn't look
            // like the browser is closing the connection before opening a new
            // one. In Rabbit MQ, the connection is closed and a new one is
            // open. Hermes is working as intended
            this.hermes._topics.forEach(t => {
              if (!`${t?.topic}`.startsWith('account-id://')) {
                this.hermes.unsubscribe(`${t?.topic}`);
              }
            });

            this.subscribeToHermes(callerID);
          } else {
            this.initializeHermes();
            this.subscribeToHermes(callerID);
          }
          removeCall(prevProps.callerID);
        } else if (isJVEnabled || isShareChatEnabled || isAviveEnabled) {
          if (this.hermes) {
            this.subscribeToHermes(callerID, prevProps.callerID);
          } else {
            this.initializeHermes();
            this.subscribeToHermes(callerID, prevProps.callerID);
          }
        }
      } else if (
        prevProps.callerID === callerID &&
        callerID !== '' &&
        !this.hermes
      ) {
        // This case occurs when using the caller_id query param
        this.initializeHermes();
        this.subscribeToHermes(callerID);
      }
    }

    // Medicalert
    // Querying by clicking on candidate
    if (
      currentMafid !== null &&
      prevProps.currentMafid !== currentMafid &&
      mafidToQuery !== currentMafid
    ) {
      // querying by mafid will update currentMafid. if they match, don't
      // query. can possibly remove this.props.mafidToQuery !==
      // this.props.currentMafid if passing in false to fetchProfile
      this.fetchProfile(currentMafid, false);
    }

    // Querying by mafid
    if (prevProps.mafidToQuery !== mafidToQuery && mafidToQuery !== null) {
      this.fetchProfile(mafidToQuery, true);
    }

    // 4.0 Alert
    // Subscribe to shermes with specific alertID when selectedAlertID change
    if (selectedAlertID && prevProps.selectedAlertID !== selectedAlertID) {
      if (prevProps.selectedAlertID) {
        this.unSubscribeAlertIDFromHermes(prevProps.selectedAlertID);
      }
      this.subscribeToHermesWithAlertID(selectedAlertID);
    }
    // unsubscribe to AlertID after user clicks on 'x' to close the data pane
    if (prevProps.selectedAlertID && selectedAlertID === null) {
      this.unSubscribeAlertIDFromHermes(prevProps.selectedAlertID);
    }

    // Shouldn't need to compare with prevProps value because `loading` will
    // always have an initial value of `false` when switching between calls
    if (currentAdrQuery.loading && !currentAdrQuery.fetched) {
      // Subscribe to `tel://${selectedCallerID}-adr`
      this.subscribeForAdr(callerID);
    }
    if (!!prevProps.callerID && prevProps.callerID !== callerID) {
      this.unsubscribeForAdr(prevProps.callerID);
    }
    if (!prevProps.currentAdrQuery.fetched && currentAdrQuery.fetched) {
      clearTimeout(this.state.adrQueryTimer);
      this.setState({ adrQueryTimer: null });
    }
  }

  componentWillUnmount() {
    if (this.hermes) {
      this.hermes.closeConnection();
    }
    const { setCallerID } = this.props;
    // TODO: need removeCall?
    setCallerID('');
    window.removeEventListener('beforeunload', () => {
      if (this.hermes) {
        this.hermes.closeConnection();
      }
    });
  }

  fetchProfile = (mafid, isQueryingByMafid) => {
    const { setMaLoadingState } = this.props;
    const maPayload = {
      type: 'subscription',
      body: {
        action: 'subscribe',
        topic: `maf-id://${mafid}`,
      },
    };

    if (isQueryingByMafid) {
      this.queryByMafid(mafid);
    } else {
      // When not querying by mafid, assume that there is always an open
      // connection due to subscribing for phone number.
      // Set loading state to true. Will be set to false when hermes returns
      // any/no data
      setMaLoadingState(true);
      if (this.hermes) {
        this.hermes.sendMessage(maPayload);
      } else {
        this.initializeHermes();
        // Subscribe by mafid is doing the same thing as subscribing by
        // te://...l&send_maf_data=true, then clicking on a candidate's
        // profile to do this.hermes.sendMessage(...)
        this.hermes.subscribe(`maf-id://${mafid}`);
      }
    }
  };

  handleCandidates = message => {
    const { setCandidates } = this.props;
    const id = message.topic.split('//')[1];
    setCandidates(message.body, id);
  };

  sendPayloadAcknowledgement = payload => {
    const ackPayload = {
      type: 'acknowledgement',
      body: payload,
    };

    if (this.hermes) {
      this.hermes.sendMessage(ackPayload);
    }
  };

  handleHermesMessages = message => {
    const {
      applicationName,
      callerID,
      callQueue,
      hasAlertsAccess,
      hasAniAliAccess,
      irpVersion,
      isJVEnabled,
      isShareChatEnabled,
    } = this.props;

    // Handles air ambulance data type
    if (message.type.includes('air_ambulance')) {
      this.handleAirAmbulanceData(message);
      return;
    }

    // Checking as early as possible since latency performance is important
    if (message.type === 'call_data.transcribed_audio.deepgram.digi') {
      this.props.handleTranscriptionRawMessage(message);
      return;
    }

    const normalizedMessage = normalizeMessage(message);

    // Formalized message type to type.category.organization.suffix in 3.6
    const messageType = get(normalizedMessage, TYPE);

    let wsMessage;
    let locationPayloadID = '',
      locationRequestID = '',
      adrPayloadID = '',
      adrRequestID = '';

    // TODO: messageType = get(wsMessage, TYPE) since it's reused. constants file for hermes message types
    if (
      get(message, TYPE) !== SUBSCRIPTION ||
      get(message, TYPE) !== LOCATION
    ) {
      wsMessage = parseWSMessage(normalizedMessage);
    }

    const adrTopic = get(normalizedMessage, TOPIC);
    let adrCallerID;
    //SAD
    if (get(wsMessage, TYPE) === DATA) {
      if (message.topic.endsWith('-adr')) {
        this.handleAdrQueryData(message, wsMessage);
      } else {
        // CIRCUS and other organizations go here
        this.handleSAData(normalizedMessage, wsMessage);
      }

      if (adrTopic) {
        adrCallerID = adrTopic.split('://')[1];
        adrRequestID = normalizedMessage.request_id;
        adrPayloadID = normalizedMessage.payload_id;
      }
      if (
        callQueue[callerID] &&
        callQueue[callerID]?.core &&
        callQueue[callerID]?.core?.locations?.length > 0 &&
        callerID === adrCallerID
      ) {
        const { mostRecentLocation } = getMostRecentLocations(
          callQueue[callerID],
        );
        const { payloadID, requestID } = isExtraInfoOrMeta(mostRecentLocation);
        locationPayloadID = payloadID;
        locationRequestID = requestID;
        trackADRPayload(
          applicationName,
          irpVersion,
          messageType,
          locationPayloadID,
          locationRequestID,
          adrRequestID,
          adrPayloadID,
        );
      }
    } else if (hasAlertsAccess && get(wsMessage, TYPE) === ALERT) {
      // alert
      this.handleNewAlerts(message.body);
    } else if (hasAlertsAccess && get(wsMessage, TYPE) === ALERT_LOG_MESSAGE) {
      this.handleUpdateLogMessage(message.body);
    } else if (get(message, TYPE) === 'alert_location') {
      this.handleAlertLocationUpdate(message);
    } else if (
      get(message, TYPE) === LOCATION &&
      get(message, 'body.latitude') &&
      get(message, 'body.longitude')
    ) {
      const isAniAli = isSourceAniAli(message.body);

      const {
        application = '',
        extra_info = {},
        created_time = '',
        location_time = '',
        call_start_time = '',
      } = message.body;

      if (isAniAli) {
        const unixTimestamp = new Date().getTime(); //milliseconds
        const websocketPayload = {
          application,
          payloadID: extra_info?.payload_id,
          requestID: extra_info?.request_id,
          rawHttpPayload: extra_info.raw_http_payload,
          timeStampFromPayload: {
            created_time,
            location_time,
            call_start_time,
            received_time: unixTimestamp,
          },
        };
        this.sendPayloadAcknowledgement(websocketPayload);
      }
      // if it is an ANI/ALI call and user does not have capability and permission,
      // do not add to the 911 queue
      if (isAniAli && !hasAniAliAccess) return;
      // add agency share chat properties to be used in share menu and markers
      if (isShareChatEnabled) {
        message.body = {
          ...message.body,
          agencyShareChat: {
            eventType: message.type,
            isCallReceived: false,
            isCallShared: false,
            isUnread: false,
            isUnseen: false,
          },
        };
      }

      const shouldHandleLocation = () => {
        // Assume user hasAniAliAccess. JV doesn't matter
        const canViewAniAli = isAniAli && hasAniAliAccess;

        // If ASC is enabled
        const isShareMessage = !!(isShareChatEnabled && get(message, 'sharer'));

        // Is a QV phone number search
        const isQueryViewDefault = !!(
          callerID && get(message, 'body.caller_id') === callerID
        );

        return [
          isJVEnabled,
          canViewAniAli,
          isShareMessage,
          isQueryViewDefault,
        ].some(bool => !!bool);
      };

      if (shouldHandleLocation()) {
        this.handleLocations(message.body);
      }
    }
    // EVENT_RECEIVE is emitted when an event has been received by the receiving peer agency
    else if (
      isShareChatEnabled &&
      get(message, TYPE) === EVENT_RECEIVE &&
      get(message, 'body.event.latitude') &&
      get(message, 'body.event.longitude') &&
      get(message, 'sharer')
    ) {
      message.body = {
        ...message.body,
        agencyShareChat: {
          ...message.body?.agencyShareChat,
          isCallReceived: true,
          isUnseen: true,
        },
        event: {
          ...message.body.event,
          agencyShareChat: {
            ...message.body.event?.agencyShareChat,
            isCallReceived: true,
            isUnseen: true,
          },
        },
      };
      this.handleLocations(message.body.event);
      this.handleAgencyShareChat(message, EVENT_RECEIVE);
    }
    // EVENT_SHARE is emitted when an event has been shared by the sharing peer agency,
    // this provides visibility to other agents in the same agency that an event has been shared
    else if (
      isShareChatEnabled &&
      get(message, TYPE) === EVENT_SHARE &&
      get(message, 'body.event.latitude') &&
      get(message, 'body.event.longitude')
    ) {
      message.body = {
        ...message.body,
        agencyShareChat: {
          ...message.body?.agencyShareChat,
          isCallShared: true,
        },
      };
      this.handleLocations(message.body.event);
      this.handleAgencyShareChat(message, EVENT_SHARE);
    }
    // PARTICIPANT_ADDED is emitted when new participants join the conversation
    else if (
      isShareChatEnabled &&
      get(message, TYPE) === PARTICIPANT_ADDED &&
      get(message, 'body.participants')
    ) {
      this.handleParticipantsAdded(message.body);
    } else if (
      get(message, TYPE) === SPATIAL &&
      isSpatialLayerDynamic(message.body)
    ) {
      this.handleDynamicLayers(message);
    }
  };

  handleDynamicLayers = payload => {
    let message = payload.body;

    if (Array.isArray(message?.data)) {
      if (message?.layer?.key === AXON_LAYERS.AXON_BODY_3) {
        this.props.handleAxonRawMessage(message);
      } else if (message?.layer?.key === EAGLE_EYE_LAYERS.EAGLE_EYE) {
        this.props.handleEagleEyeRawMessage(payload);
      }

      // HACK: Axon and Eagle Eye payloads come in as collections (snapshots). Take one spatial
      // object here and retrofit the payload so the Menu gets populated using
      // existing handling (spatial object on arrival vs arrays of spatials);
      // add the excludeFromDrawing flag and makes sure the same external_id is
      // always used to avoid any side-effects. To be removed after the Menu
      // is refactored to have its own redux slice. See the flag used in
      // useDrawSpatialLayer, where the layer is eventually ignored.
      const spatialObject = { ...(message?.data[0] || {}) };
      const placeholderId = `${message?.layer?.key}-placeholder`;
      spatialObject.external_id = placeholderId;
      message = {
        ...(message?.data[0] || {}),
        layer: {
          ...(message?.layer || {}),
          spatial_objects: [spatialObject],
        },
        external_id: placeholderId,
        excludeFromDrawing: true,
      };
    } else if (
      message?.layer?.key === AXON_LAYERS.AXON_BODY_3 ||
      message?.layer?.key === EAGLE_EYE_LAYERS.EAGLE_EYE
    ) {
      // If the message.data is not an array and it's axon_police_tracking,
      // render its objects as standalone spatial objects (ordinary DSL).
      // Even if not the expected batch format.
      message.layer.excludeFromDrawing = false;
    }

    const { addDynamicLayer, dynamicLayers, updateDynamicLayer } = this.props;
    if (getExistingDynamicObject(dynamicLayers, message)) {
      updateDynamicLayer(message);
    } else {
      addDynamicLayer(message);
    }
  };

  handleNewAlerts = messageBody => {
    const { addAlertToQueue, callerID, callQueue, resetIDWithNewAlert } =
      this.props;

    addAlertToQueue(messageBody);

    const hasRSPDeckGLCapability =
      !!this.props.currentPSAP?.activeCapabilities?.[
        GOOGLE_MAPS_DECKGL_MARKERS
      ];
    const rspDeckGL = this.flags?.google_maps_updates;

    // Execute legacy code when emergency flag is off OR capability is off
    if (!rspDeckGL || !hasRSPDeckGLCapability) {
      const hasLocation =
        callQueue[callerID] && callQueue[callerID]?.core?.locations?.length > 0;
      // If viewing a call, reset the `IDWithNewAlert`. See MC-24413
      if (callerID && hasLocation) {
        resetIDWithNewAlert();
      }
    }
  };

  handleAlertLocationUpdate = message => {
    this.props.updateAlert(message);
  };

  handleLocations = location => {
    const {
      addLocation,
      addLocToQueueSuccess,
      isJVEnabled,
      isShareChatEnabled,
      currentPSAP,
    } = this.props;

    const newLocation = {
      ...location,
    };

    if (typeof location?.source === 'string') {
      newLocation.source = location.source.toLowerCase();
    }

    // Filter out TCAPI locations if authority doesn't have TT911 capability
    const isTCAPILocation = newLocation.source === SOURCE_TCAPI;
    const hasTT911Capability = !!currentPSAP?.activeCapabilities?.[TT911];

    if (isTCAPILocation && !hasTT911Capability) {
      return;
    }

    const addLocationToQueue =
      isJVEnabled || isShareChatEnabled ? addLocToQueueSuccess : addLocation;

    return addLocationToQueue(newLocation);
  };

  // TODO Unused? To be removed
  handleMedicalert = (message, wsMessage) => {
    const { setMaLoadingState } = this.props;
    // If Medicalert requests return 5xx, don't dispatch/handle anything and
    // reset loading state
    if (parseInt(message.status, 10) < 500) {
      if (
        wsMessage.organization === 'medicalert' &&
        wsMessage.suffix === 'candidates'
      ) {
        this.handleCandidates(message);
      } else if (
        wsMessage.organization === 'medicalert' &&
        wsMessage.suffix === 'profile'
      ) {
        // Handle Medicalert Data
        this.handleProfile(message);
      }
    }
    setMaLoadingState(false);
  };

  handleProfile = message => {
    const { callerID, setProfile } = this.props;
    setProfile(message, callerID);
  };

  handleAvive = (message, wsMessage) => {
    const { handleAviveRawMessage } = this.props;
    handleAviveRawMessage({ raw: message, meta: wsMessage });
  };

  handleSAData = (message = {}, wsMessage) => {
    const { addSAData, addAdrData, permissions } = this.props;

    const normalizedPermissions = permissions.map(permission => {
      if (permission === 'PROFILE_INFO') {
        return 'medicalert';
      }
      return permission.replace(/^_/, '').toLowerCase();
    });

    if (wsMessage.organization === 'avive') {
      // Handle MapCard render.
      this.handleAvive(message, wsMessage);
      // NOTE this ADR is in addition to the individual spatial objects that
      // render within MapCards - handled inside of useDrawSpatialLayers().
      // This message is for display within the call pane.
      addSAData(message);
    }

    if (normalizedPermissions.includes(wsMessage.organization)) {
      addSAData(message);
    }
    addAdrData(message);
  };

  /**
   * Function to handle messages with topic `tel://${callerID}-adr`. It is
   * similar to handleSAData
   * @param {Object} message - hermes message
   * @param {Object} wsMessage - parsed message type
   */
  handleAdrQueryData = (message, wsMessage) => {
    const { setAdrQueryData, addAdrData, permissions } = this.props;

    const normalizedPermissions = permissions.map(permission => {
      if (permission === 'PROFILE_INFO') {
        return 'medicalert';
      }
      return permission.replace(/^_/, '').toLowerCase();
    });

    if (normalizedPermissions.includes(wsMessage.organization)) {
      setAdrQueryData(message);
    }
    addAdrData(message);
  };

  handleUpdateLogMessage = messageBody => {
    const {
      alerts,
      getAlertDetails,
      getLogMessageUpdatedStatus,
      currentApplication,
      currentOrgName,
    } = this.props;

    // An alert can get created in one region but the event scheduler in the other region can
    // pick up the timeout job, causing the `alert_log_message` type message arrive without the
    // `alert` type message. Since the alert data is incomplete, it can cause the application to
    // crash. To prevent this from happening, if the `messageBody.alert_id` doesn't exist in the
    // `alerts` object, fetch for the alert's most updated information.
    alerts[messageBody.alert_id]
      ? getLogMessageUpdatedStatus(messageBody, currentApplication)
      : getAlertDetails(
          currentApplication,
          currentOrgName,
          messageBody.alert_id,
        );
  };

  handleAirAmbulanceData = message => {
    const { addAndUpdateAirAmbulanceRequest, callerID, callQueue } = this.props;
    const hasMostRecentLocation = !!getMostRecentLoc(callQueue[callerID]);
    addAndUpdateAirAmbulanceRequest({ message, hasMostRecentLocation });
  };

  handleAgencyShareChat = (message, messageType) => {
    const { setAgencyShareChatToastReceived, setCallReceived, setCallShared } =
      this.props;

    // The error "cannot read properties of undefined (reading eventChatParticipants)" has been
    // observed in the sandbox prospector authority tests. This is not expected to occur in a real
    // production scenario. The error occurs because "eventChatParticipants" is missing in the
    // agency share/chat "postShareEmergencyCall" post request payload, which leads to missing data
    // in the shared or received messages in Hermes.
    if (messageType === EVENT_RECEIVE) {
      setAgencyShareChatToastReceived({
        callSource: message.body.event.source,
        isToastVisible: true,
        receivedCallerID: message.body.event.caller_id,
        sharerAgencyName: message.body.event?.agencyShareChat?.eventSharedBy,
      });
      setCallReceived({
        additionalData: message.body?.adr_data,
        callerID: message.body.event.caller_id,
        callSharer: message.sharer,
        chatParticipants:
          message.body.event?.agencyShareChat?.eventChatParticipants,
        conversationID: message.body.conversation_id,
      });
    } else if (messageType === EVENT_SHARE) {
      setCallShared({
        additionalData: message.body?.adr_data,
        callerID: message.body.event.caller_id,
        chatParticipants:
          message.body.event?.agencyShareChat?.eventChatParticipants,
        conversationID: message.body.conversation_id,
      });
    }
  };

  handleParticipantsAdded = messageBody => {
    const { setCallChatParticipants } = this.props;
    const dataURI = parseURI(messageBody.uri);
    let callerID = '';

    if (dataURI.type === 'tel') callerID = dataURI.value;

    setCallChatParticipants({
      callerID: callerID,
      chatParticipants: messageBody.participants,
    });
  };

  initializeHermes = (isJVEnabled = false, isShareChatEnabled = false) => {
    const {
      accessToken,
      currentPSAP,
      hasAniAliAccess,
      hasMAaccess,
      hasAlertsAccess,
      refreshToken,
      pathName,
      setInitializedHermesState,
      isAviveEnabled = false,
      isAxonEnabled = false,
      wsRef,
      organizationID,
      applicationName,
      hasHermesV2Access,
    } = this.props;

    const hermesIrpVersion =
      pathName === '/irp-query-interface' || pathName === '/irp-home'
        ? '2'
        : isValidPhone(getUrlParameter('caller_id'))
          ? '1'
          : '0';

    if (!this.hermes) {
      this.hermes = new StreamWS(accessToken, refreshToken, {
        host: webSocketHost,
        irpVersion: hermesIrpVersion,
        organizationID,
        applicationName,
        hasHermesV2Access,
      });

      // Expose the hermes instance for debugging and automation.
      const hermes = this.hermes;
      exportToGlobalNamespace('streamWS', {
        instance: hermes,
        handle: (...props) => {
          hermes._callback(...props);
          if (Object.keys(hermes._callbacks).length) {
            Object.keys(hermes._callbacks).forEach(callbackName => {
              hermes._callbacks[callbackName](...props);
            });
          }
        },
      });

      wsRef.current = this.hermes;

      this.hermes.onMessage(message => {
        this.handleHermesMessages(message);
      });

      if (
        hasAlertsAccess ||
        hasAniAliAccess ||
        isAviveEnabled ||
        isAxonEnabled ||
        isJVEnabled ||
        isShareChatEnabled
      ) {
        setInitializedHermesState(true);
        this.hermes.subscribe(`account-id://${currentPSAP.account_id}`);
      } else if (!isJVEnabled && hasMAaccess) {
        setMaLoadingState(true);
      }
    }
  };

  queryByMafid = mafid => {
    const maPayload = {
      type: 'subscription',
      body: {
        action: 'subscribe',
        topic: `maf-id://${mafid}`,
      },
    };
    const { accessToken } = this.props;
    if (accessToken) {
      if (this.hermes) {
        // Assume a hermes connection was already opened by location query
        // or query by phone.
        this.hermes.sendMessage(maPayload);
      } else {
        this.initializeHermes();

        this.hermes.onMessage(message => {
          this.handleHermesMessages(message);
        });

        // Unsubscribe from previous mafid
        this.hermes._topics.forEach(t => {
          if (`${t?.topic}`.includes('maf-id://')) {
            this.hermes.unsubscribe(`${t?.topic}`);
          }
        });
        // Subscribe by mafid is doing the same thing as subscribing by
        // te://...l&send_maf_data=true, then clicking on a candidate's
        // profile to do this.hermes.sendMessage(...)
        this.hermes.subscribe(`maf-id://${mafid}`);
      }
    }
  };

  subscribeToHermes = (callerID, prevCallerID) => {
    const { hasMAaccess, setMaLoadingState, avive = {} } = this.props;

    const { mostRecentLocation } = getMostRecentLocations(
      this.props.callQueue[callerID],
    );

    const data = {
      location: {
        latitude: mostRecentLocation && parseFloat(mostRecentLocation.latitude),
        longitude:
          mostRecentLocation && parseFloat(mostRecentLocation.longitude),
      },
    };

    // When Avive search is active, do not unsubscribe to the previous
    // phone number. This can happen when an user searches for AED and
    // navigates away while the search is happening. Instead leave the
    // connection open until the search is over. When it's over the
    // isSearchActive will be set to false and normal unsubscribing behavior
    // can continue.
    const prevTelKey = `tel://${prevCallerID}`;
    const isAviveActive = avive?.statuses[prevTelKey]?.states?.isSearchActive;

    if (prevCallerID && !isAviveActive) {
      this.hermes.unsubscribe(prevTelKey);
    }
    if (callerID) {
      this.hermes.subscribe(`tel://${callerID}`, data);
    }
    if (hasMAaccess) {
      setMaLoadingState(true);
    }
  };

  /**
   * Subscribe to Hermes with alert id when the user clicks on a specific alert
   * @param {string} alertID - selected alert ID
   *
   */
  subscribeToHermesWithAlertID = alertID => {
    this.hermes.subscribe(`alert://${alertID}`);
  };

  /**
   * Subscribe for ADR
   * @param {String} callerID - selectedCallerID
   */
  subscribeForAdr = callerID => {
    const topic = `tel://${callerID}-adr`;
    const topicExist = Array.from(this.hermes._topics).find(
      t => t.topic === topic,
    );
    if (!topicExist) {
      this.hermes.subscribe(topic);
      const timer = setTimeout(() => {
        // Assume failed to fetch after 20 seconds
        this.props.setAdrQueryStatus({
          callerID,
          loading: false,
          fetched: true,
          error: true,
        });
        this.unsubscribeForAdr(callerID);
      }, 20000);
      this.setState({ adrQueryTimer: timer });
    }
  };

  /**
   * Unsubscribe for ADR
   * @param {String} prevCallerID - caller ID to unsubscribe for
   */
  unsubscribeForAdr = prevCallerID => {
    const topic = `tel://${prevCallerID}-adr`;
    const topicExist = Array.from(this.hermes._topics).find(
      t => t.topic === topic,
    );
    if (topicExist) {
      this.hermes.unsubscribe(topic);
      clearTimeout(this.state.adrQueryTimer);
      this.setState(
        {
          adrQueryTimer: null,
        },
        () => {
          this.props.setAdrQueryStatus({
            callerID: prevCallerID,
            loading: false,
            fetched: this.props.callQueue[prevCallerID].adrQuery.fetched,
            hide: this.props.callQueue[prevCallerID].adrQuery.hide,
            error: this.props.callQueue[prevCallerID].adrQuery.error,
          });
        },
      );
    }
  };

  /**
   * unSubscribe to Hermes from the selected alert id when the user clicks on a different alert
   * @param {string} alertID - previous selected alert ID
   *
   */
  unSubscribeAlertIDFromHermes = alertID => {
    this.hermes.unsubscribe(`alert://${alertID}`);
  };

  render() {
    return <div className="HermesComponent" />;
  }
}

Hermes.propTypes = {
  avive: PropTypes.shape({}),
  addAlertToQueue: PropTypes.func,
  addLocation: PropTypes.func,
  addLocToQueueSuccess: PropTypes.func,
  callerID: PropTypes.string,
  currentAdrQuery: PropTypes.object,
  currentMafid: PropTypes.string,
  currentPSAP: PropTypes.shape(),
  dynamicLayers: PropTypes.array,
  hasMAaccess: PropTypes.bool,
  hasAniAliAccess: PropTypes.bool,
  isJVEnabled: PropTypes.bool,
  isJVHermesInitialized: PropTypes.bool,
  isShareChatEnabled: PropTypes.bool,
  mafidToQuery: PropTypes.string,
  accessToken: PropTypes.string,
  setAgencyShareChatToastReceived: PropTypes.func,
  setCallChatParticipants: PropTypes.func,
  setCallerID: PropTypes.func,
  setCallReceived: PropTypes.func,
  setCallShared: PropTypes.func,
  setCandidates: PropTypes.func,
  setInitializedHermesState: PropTypes.func,
  setMaLoadingState: PropTypes.func,
  setProfile: PropTypes.func,
  addAndUpdateAirAmbulanceRequest: PropTypes.func,
};

Hermes.defaultProps = {
  avive: {},
  accessToken: null,
  addLocation: () => {},
  addLocToQueueSuccess: () => {},
  callerID: '',
  currentAdrQuery: {},
  currentMafid: null,
  currentPSAP: {},
  dynamicLayers: [],
  hasAniAliAccess: false,
  hasMAaccess: false,
  isJVEnabled: false,
  isShareChatEnabled: false,
  isHendrixHermesInitialized: false,
  mafidToQuery: null,
  refreshToken: null,
  removeCall: () => {},
  resetIDWithNewAlert: () => {},
  updateAlert: () => {},
  setAdrQueryData: () => {},
  setAdrQueryStatus: () => {},
  setAgencyShareChatToastReceived: () => {},
  setCallChatParticipants: () => {},
  setCallerID: () => {},
  setCallReceived: () => {},
  setCallShared: () => {},
  setCandidates: () => {},
  setInitializedHermesState: () => {},
  setMaLoadingState: () => {},
  setProfile: () => {},
  addAndUpdateAirAmbulanceRequest: () => {},
};

const mapStateToProps = state => {
  const selectedCallerID = state.emergencyCalls.selectedCallerID;
  return {
    avive: state.avive,
    callerID: selectedCallerID,
    callQueue: state.emergencyCalls.callQueue,
    currentAdrQuery:
      state.emergencyCalls.callQueue?.[selectedCallerID]?.adrQuery,
    currentPSAP: state.psaps.currentPSAP,
    dynamicLayers: state.spatialLayers.dynamicLayers,
    isHendrixHermesInitialized: state.emergencyCalls.isHendrixHermesInitialized,
    accessToken: state.sinatra.auth.tokens.token,
    refreshToken: state.sinatra.auth.tokens.refresh_token,
    currentApplication: state.sinatra.user.currentRole.application,
    currentOrgName: state.sinatra.user.currentOrg.name,
    selectedAlertID: state.incidents.selectedAlertID,
    irpVersion: state.sinatra.irp.irpVersion,
    alerts: state.incidents.alerts,
  };
};
const mapDispatchToProps = {
  addAlertToQueue,
  addAdrData,
  addDynamicLayer,
  addSAData,
  addLocation,
  addLocToQueueSuccess,
  getAlertDetails,
  getLogMessageUpdatedStatus,
  removeCall,
  resetIDWithNewAlert,
  setAdrQueryData,
  setAdrQueryStatus,
  setAgencyShareChatToastReceived,
  setCallChatParticipants,
  setCallerID,
  setCallReceived,
  setCallShared,
  setCandidates,
  setInitializedHermesState,
  setMafidToQuery,
  setMaLoadingState,
  setProfile,
  updateAlert,
  updateDynamicLayer,
  handleAviveRawMessage,
  handleAxonRawMessage,
  handleEagleEyeRawMessage,
  handleTranscriptionRawMessage,
  addAndUpdateAirAmbulanceRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(Hermes);
